import {
  passGoBackInTime, passGoForwardInTime,
  handleCanGoBackward, handleCanGoForward, passTimeTravelSnapshot, handleGetRunMode,
} from "../main2worker"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faClock,
  faFastBackward,
  faFastForward,
  faLayerGroup,
  faPause,
  faPlay,
} from "@fortawesome/free-solid-svg-icons"
import { handleSetCPUState } from "../controller"
import { handleFileSave } from "../savestate"
import { RUN_MODE } from "../../common/utility"
import { isGameMode } from "../ui_settings"
import { useTranslation } from "../../i18n/useTranslation"
import type { RetroControlMetadata } from "../retro/retromenucontext"
import { createControlContext } from "../retro/retromenucontext"
import { ControlRegistry } from "./controlregistry"
import { controlsFromJson, type RetroControlBindings } from "../retro/retrocontrolmetadata"

const emulatorStarted = () => {
  const runMode = handleGetRunMode()
  return runMode !== RUN_MODE.IDLE && runMode !== RUN_MODE.NEED_BOOT
}

const debugBindings: RetroControlBindings = {
  "snapshot.back": {
    action: passGoBackInTime,
    selectable: () => emulatorStarted() && handleCanGoBackward(),
  },
  "snapshot.take": {
    action: passTimeTravelSnapshot,
    selectable: emulatorStarted,
  },
  "snapshot.forward": {
    action: passGoForwardInTime,
    selectable: () => emulatorStarted() && handleCanGoForward(),
  },
  "snapshot.saveState": {
    action: () => handleFileSave(true),
    selectable: emulatorStarted,
  },
  "emulator.pause": {
    label: context => context.t(handleGetRunMode() === RUN_MODE.PAUSED
      ? "debugControls.resume"
      : "debugControls.pause"),
    action: context => {
      handleSetCPUState(handleGetRunMode() === RUN_MODE.PAUSED ? RUN_MODE.RUNNING : RUN_MODE.PAUSED)
      context.displayProps.updateDisplay()
    },
    selectable: () => handleGetRunMode() !== RUN_MODE.IDLE,
  },
}

export const retroDebugControls: RetroControlMetadata[] = controlsFromJson("debug", debugBindings)

const debugControlRegistry = new ControlRegistry(retroDebugControls)

const DebugButtons = (props: DisplayProps) => {
  const { t, language, changeLanguage } = useTranslation()
  const runMode = handleGetRunMode()
  const machineControls = debugControlRegistry.resolve(
    createControlContext(props, t, language, changeLanguage),
    "machine",
  )
  const controls = machineControls.filter(item => item.id !== "machine.timeMachine")
  const control = (id: string) => controls.find(item => item.id === id)!
  const back = control("snapshot.back")
  const take = control("snapshot.take")
  const forward = control("snapshot.forward")
  const save = control("snapshot.saveState")
  const pause = control("emulator.pause")
  return <span className="flex-row">
    <div className="flex-row" id="tour-snapshot">
      <button className="push-button"
        title={back.label}
        onClick={back.action}
        disabled={back.selectable === false}>
        <FontAwesomeIcon icon={faFastBackward} />
      </button>
      <button className="push-button"
        title={take.label}
        onClick={take.action}
        disabled={take.selectable === false}>
        <FontAwesomeIcon icon={faClock} />
      </button>
      <button className="push-button"
        title={forward.label}
        onClick={forward.action}
        disabled={forward.selectable === false}>
        <FontAwesomeIcon icon={faFastForward} />
      </button>
      {!isGameMode() && <button className="push-button"
        title={save.label}
        onClick={save.action}
        disabled={save.selectable === false}>
        <FontAwesomeIcon icon={faLayerGroup} />
      </button>}
    </div>
    <button className="push-button" id="tour-pause-button"
      title={pause.label}
      onClick={pause.action}
      disabled={pause.selectable === false}>
      {runMode === RUN_MODE.PAUSED ?
        <FontAwesomeIcon icon={faPlay} /> :
        <FontAwesomeIcon icon={faPause} />}
    </button>
  </span>
}

export default DebugButtons
